<template>
  <div
    class="seo-inter-hub-skeleton inter-level1"
    aria-busy="true"
    aria-live="polite">
    <!-- Même structure que PrepeersBreadcrumb sur les pages hub -->
    <nav
      v-if="props.showBreadcrumb"
      class="breadcrumb"
      aria-hidden="true">
      <ol class="breadcrumb__list">
        <li class="breadcrumb__list-item">
          <PrePeersSkeleton
            variant="text"
            class="seo-inter-hub-skeleton__breadcrumb-bar"
            height="1rem"
            width="min(240px, 70vw)" />
        </li>
      </ol>
    </nav>

    <!-- Hero : h1 + intro + divider (classes globales hero-section) -->
    <section class="hero-section">
      <div class="container">
        <PrePeersSkeleton
          variant="text"
          class="seo-inter-hub-skeleton__h1"
          height="clamp(1.75rem, 4vw, 2.25rem)"
          width="min(640px, 92%)" />
        <div class="seo-inter-hub-skeleton__intro">
          <PrePeersSkeleton
            variant="text"
            class="seo-inter-hub-skeleton__intro-line"
            width="100%" />
          <PrePeersSkeleton
            variant="text"
            class="seo-inter-hub-skeleton__intro-line"
            width="94%" />
          <PrePeersSkeleton
            variant="text"
            class="seo-inter-hub-skeleton__intro-line"
            width="88%" />
          <PrePeersSkeleton
            variant="text"
            class="seo-inter-hub-skeleton__intro-line seo-inter-hub-skeleton__intro-line--short"
            width="72%" />
        </div>
        <div
          class="hero-section__line seo-inter-hub-skeleton__line"
          aria-hidden="true" />
      </div>
    </section>

    <!-- Vidéo : titre h2 + wrapper 16/9 (classes video-section) -->
    <section
      v-if="props.showVideo"
      class="video-section">
      <div class="container">
        <PrePeersSkeleton
          variant="text"
          class="seo-inter-hub-skeleton__video-title"
          height="1.75rem"
          width="min(720px, 94%)" />
        <div class="video-section__wrapper seo-inter-hub-skeleton__video-wrapper">
          <PrePeersSkeleton variant="block" />
        </div>
      </div>
    </section>

    <section
      v-for="index in props.navSectionCount"
      :key="`nav-${index}`"
      class="level-nav-section">
      <div class="container">
        <PrePeersSkeleton
          variant="text"
          class="seo-inter-hub-skeleton__nav-title"
          height="1.5rem"
          width="min(360px, 85%)" />
        <div class="level-nav__list seo-inter-hub-skeleton__nav-list">
          <PrePeersSkeleton
            v-for="linkIndex in 6"
            :key="`nav-${index}-link-${linkIndex}`"
            variant="text"
            class="seo-inter-hub-skeleton__nav-link"
            height="2.75rem"
            :width="navLinkWidths[(linkIndex - 1) % navLinkWidths.length]" />
        </div>
      </div>
    </section>

    <section
      v-if="props.showContentSection"
      class="content-section">
      <div class="container seo-inter-hub-skeleton__content">
        <PrePeersSkeleton
          v-for="lineIndex in 5"
          :key="`content-${lineIndex}`"
          variant="text"
          class="seo-inter-hub-skeleton__content-line"
          :width="lineIndex === 5 ? '55%' : '100%'" />
      </div>
    </section>

    <section
      v-for="gridIndex in props.mediaGridCount"
      :key="`grid-${gridIndex}`"
      class="media-grid-section">
      <div class="container">
        <PrePeersSkeleton
          variant="text"
          class="seo-inter-hub-skeleton__media-title"
          height="1.5rem"
          width="min(360px, 85%)" />
        <PrePeersSkeleton
          variant="text"
          class="seo-inter-hub-skeleton__media-desc"
          width="min(520px, 90%)" />
        <div class="media-grid__cards seo-inter-hub-skeleton__cards">
          <PrePeersSkeleton
            v-for="cardIndex in 3"
            :key="`grid-${gridIndex}-card-${cardIndex}`"
            variant="block"
            class="seo-inter-hub-skeleton__card" />
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import PrePeersSkeleton from '~/components/ui/PrePeersSkeleton.vue'
import type { SeoInterHubSkeletonProps } from '~/types/SeoInterHubSkeleton'

/**
 * Définit les props du composant SeoInterHubSkeleton.
 */
const props: SeoInterHubSkeletonProps = defineProps({
  showBreadcrumb: {
    type: Boolean,
    default: true,
  },
  showVideo: {
    type: Boolean,
    default: true,
  },
  navSectionCount: {
    type: Number,
    default: 2,
  },
  showContentSection: {
    type: Boolean,
    default: false,
  },
  mediaGridCount: {
    type: Number,
    default: 0,
  },
})

const navLinkWidths: string[] = ['108px', '124px', '96px', '132px', '112px', '118px']
</script>

<style scoped>
/* Fil d'Ariane : barre centrée dans le bandeau .breadcrumb */
.seo-inter-hub-skeleton__breadcrumb-bar {
  margin-inline: auto;
}

/* H1 : même marge basse que .hero-section__h1 */
.seo-inter-hub-skeleton__h1 {
  margin-inline: auto;
  margin-bottom: var(--space-8);
}

/* Intro : colonne centrée, largeur proche du texte réel */
.seo-inter-hub-skeleton__intro {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  max-width: 42rem;
  margin: 0 auto var(--space-8);
}

.seo-inter-hub-skeleton__intro-line {
  margin-inline: auto;
}

.seo-inter-hub-skeleton__intro-line--short {
  align-self: center;
}

/* Divider : reprend .hero-section__line (150×4) avec shimmer */
.seo-inter-hub-skeleton__line {
  background: rgba(255, 255, 255, 0.08);
  animation: seo-inter-hub-skeleton-pulse 2.8s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .seo-inter-hub-skeleton__line {
    animation: none;
  }
}

/* Titre vidéo : centré comme .video-section__title */
.seo-inter-hub-skeleton__video-title {
  display: block;
  margin-inline: auto;
  margin-bottom: var(--space-12);
}

.seo-inter-hub-skeleton__video-wrapper :deep(.prepeers-skeleton) {
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

/* Nav : titres et pills centrés */
.seo-inter-hub-skeleton__nav-title {
  display: block;
  margin-inline: auto;
  margin-bottom: var(--space-8);
}

.seo-inter-hub-skeleton__nav-list {
  justify-content: center;
}

.seo-inter-hub-skeleton__nav-link {
  flex-shrink: 0;
  border-radius: var(--radius-md);
}

/* Contenu riche */
.seo-inter-hub-skeleton__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  max-width: var(--container-lg);
  margin-inline: auto;
}

.seo-inter-hub-skeleton__content-line {
  margin-inline: auto;
}

/* Grilles média */
.seo-inter-hub-skeleton__media-title,
.seo-inter-hub-skeleton__media-desc {
  display: block;
  margin-inline: auto;
}

.seo-inter-hub-skeleton__media-title {
  margin-bottom: var(--space-6);
}

.seo-inter-hub-skeleton__media-desc {
  margin-bottom: var(--space-8);
}

.seo-inter-hub-skeleton__cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-6);
}

.seo-inter-hub-skeleton__card {
  min-height: 220px;
  border-radius: var(--radius-lg);
}

@keyframes seo-inter-hub-skeleton-pulse {
  0%,
  100% {
    opacity: 0.45;
  }

  50% {
    opacity: 0.85;
  }
}

@media (min-width: 1024px) {
  .seo-inter-hub-skeleton__h1 {
    margin-bottom: var(--space-6);
  }

  .seo-inter-hub-skeleton__intro {
    margin-bottom: var(--space-6);
  }

  .seo-inter-hub-skeleton__video-title {
    margin-bottom: var(--space-8);
  }
}

@media (max-width: 1024px) {
  .seo-inter-hub-skeleton__cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .seo-inter-hub-skeleton__cards {
    grid-template-columns: 1fr;
  }
}
</style>
